import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ClientsAndAccounts = () => (
  <Page
    title="Clients, Engagement Letters, and the Client Portal"
    intro="Client records tie events, quotes, invoices, and documents to the organisations you work for. Client contacts can register for a portal account, which you approve and link to the right organisation."
  >
    <Section title="Client records">
      <Bullets>
        <Bullet>
          <DocLink url="/clients" text="Clients" /> shows your organisations
          as cards: each one carries the client's type, base location, and
          town, counts of upcoming events, total events, and contacts, the
          next event (linked), the account manager, an engagement letter chip
          (valid, expiring within 60 days, or expired), and any outstanding
          invoice balance. Use <strong>Add New</strong> to create a client —
          event clients use the <strong>Events</strong> organisation type.
        </Bullet>
        <Bullet>
          Stat tiles across the top total your <strong>Clients</strong>,{' '}
          <strong>Upcoming events</strong>, <strong>Outstanding</strong>{' '}
          invoice value, and <strong>Expired engagement letters</strong>.
          Below them, filter by search text or by{' '}
          <strong>Account manager</strong> (Anyone, My clients, or
          Unassigned), and sort by name, next event, outstanding balance, or
          engagement letter expiry. The eye icon shows or hides archived
          clients — archived ones carry an <strong>Archived</strong> chip.
        </Bullet>
        <Bullet>
          A client record shows their information, contacts, events (with
          status, EMP, and paid flags), invoices, documents, and a notes log.
          Events-type clients also get a summary card with their next and
          last event, invoices due, and engagement letter expiry.
        </Bullet>
        <Bullet>
          PTS clients' records also carry <strong>PTS Contract &amp;
          Tariff</strong>, <strong>PTS Performance</strong>, and{' '}
          <strong>Hospital Portal Access</strong> sections — see{' '}
          <DocLink
            docsPage
            url="/patient-transport/organisations"
            text="PTS Organisations"
          />
          .
        </Bullet>
        <Bullet>
          A client's base location controls which users can see the record —
          only staff with that base location selected.
        </Bullet>
      </Bullets>
      <Screenshot url="events/clients-page.png">
        The Clients page with its stat tiles, filters, and a card for each
        client organisation.
      </Screenshot>
      <Info title="Who can do this?">
        Client records, contacts, engagement letters, and account approvals
        all need the <strong>Event Planning</strong> team (site
        administrators always have access). The Customers sidebar section
        itself is shown to Event Planning and site administrator users.
      </Info>
    </Section>

    <Section title="Account managers">
      <Bullets>
        <Bullet>
          Each client can have an <strong>account manager</strong> — shown in
          the client record's hero and changed from a small dialog there. The
          picker offers staff on the <strong>Event Planning</strong> team.
        </Bullet>
        <Bullet>
          The account manager gets a notification-bell alert whenever a new
          note, event, or PTS booking is added for their client, so nothing on
          their accounts goes unnoticed.
        </Bullet>
        <Bullet>
          On the Clients page, the <strong>My clients</strong> filter narrows
          the list to the ones you manage, and <strong>Unassigned</strong>{' '}
          finds clients still needing an owner.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Engagement letters">
      <Bullets>
        <Bullet>
          Click <strong>Engagement Letter</strong> on the client record, set
          the start and end dates (defaulting to one year), and submit.
          AmbuNet generates the letter as a PDF, files it in the client's
          Documents grid, and logs it against the client.
        </Bullet>
        <Bullet>
          The expiry date appears on the client list and record so lapsed
          letters stand out. There's no renew action as such — create a new
          letter when the current one is due to expire.
        </Bullet>
        <Bullet>
          Current letters are also visible to the client on their portal
          dashboard.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Approving client portal accounts">
      <Step number="1" title="A contact registers">
        Client contacts sign themselves up from the registration page,
        verifying their email. New accounts wait for approval and can't see
        anything until it's granted.
      </Step>
      <Step number="2" title="Review the application">
        A <strong>Client Accounts</strong> card appears on your dashboard
        when accounts are waiting, linking to{' '}
        <DocLink url="/pending-client-accounts" text="Pending Client Accounts" />
        . Open an applicant to see their name, job title, contact details,
        and the organisation name exactly as they typed it.
      </Step>
      <Step number="3" title="Approve or reject">
        Pick the correct organisation in{' '}
        <strong>Assign to Organisation</strong> and click{' '}
        <strong>Approve</strong> — the contact is emailed a sign-in link.{' '}
        <strong>Reject</strong> removes the account entirely.
      </Step>
      <Warning title="Check the organisation carefully">
        Approval links the person to that organisation's events, invoices,
        and incident reports. The organisation name on the application is
        free text they typed — match it to the right client record before
        approving.
      </Warning>
    </Section>

    <Section title="What clients see in the portal">
      <Bullets>
        <Bullet>
          A dashboard with their upcoming events, outstanding invoices, and
          current engagement letters.
        </Bullet>
        <Bullet>
          Their events, each with the event details, a primary contact
          picker, a message thread with your planning team, and the Event
          Medical Plan — visible once approved, or earlier if you turn on{' '}
          <strong>EMP Visible to Client (including Draft)</strong> on the
          event.
        </Bullet>
        <Bullet>
          Health and safety incident reports for their events, and a
          post-event feedback form that feeds the Client Feedback field of
          your post-event analysis.
        </Bullet>
        <Bullet>Quotes are not shown in the portal.</Bullet>
      </Bullets>
      <Info title="Invoicing">
        Per-client invoices are listed on the client record, and new invoices
        are raised from the event (or PTS booking) they belong to. See the
        Finance documentation section for the full invoicing workflow.
      </Info>
    </Section>
  </Page>
)

export default ClientsAndAccounts
